import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { IconGridSidebar } from '../components/IconGridSidebar';
import { useMessagesUnread } from '../hooks/useMessagesUnread';
import { 
  Wrench, Search, MapPin, Calculator, CheckSquare, ShoppingBag, Store, Key
} from 'lucide-react';
import Dashboard2 from '../Dashboard2';
import OnboardingChecklist from '../components/OnboardingChecklist';
import WallPage from './WallPage';
import HomeTasks from './HomeTasks';
import HomeShopping from './HomeShopping';
import HomeBudget from './HomeBudget';
import HomeVehicles from './HomeVehicles';
import HomeEquipment from './HomeEquipment';
import HomePets from './HomePets';
import MyQR from './MyQR';
import StaffDirectory from './StaffDirectory';
import ReservationManager from './ReservationManager';
import TurnoverCoordination from './TurnoverCoordination';
import StaffPayroll from './StaffPayroll';
import NotificationsCenter from './NotificationsCenter';
import Employees from './Employees';
import InviteMember from './InviteMember';
import BuildingUnits from './BuildingUnits';
import Expenses from './Expenses';
import Voting from './Voting';
import Calendar from './Calendar';
import Announcements from './Announcements';
import FinancialReports from './FinancialReports';
import Morosos from './Morosos';
import Amenities from './Amenities';
import QuejasYSugerencias from './QuejasYSugerencias';
import Reglamentos from './Reglamentos';
import NumerosEmergencia from './NumerosEmergencia';
import Marketplace from './Marketplace';
import SupplierDirectory from './SupplierDirectory';
import SupplierDashboard from './SupplierDashboard';
import Visitors from './Visitors';
import LlaveDigital from './LlaveDigital';
import AdminJoinRequests from './AdminJoinRequests';
import ReporteVisitas from './ReporteVisitas';
import CajaChica from './CajaChica';
import PanicButton from './PanicButton';
import WorkOrders from './WorkOrders';
import Maintenance from './Maintenance';
import CondoEquipment from './CondoEquipment';
import VerifyQR from './VerifyQR';
import TravelNotices from './TravelNotices';
import RosterVecinos from './RosterVecinos';
import EdicionPerfil from './EdicionPerfil';
import QrAuditPage from './QrAuditPage';
// (Jaime 2026-09-12) Unified Mi Acceso: /building/resident-qr now renders MyQR
// (same page as /home/my-qr). ResidentQR.tsx stays on disk but is unreferenced.

import { DESKTOP_NAVIGATION, HAMBURGER_NAVIGATION, SERVICES_NAVIGATION, filterNavigation, entitlementForStandaloneHome, isEmployeeContextForLens, isStandaloneHomeUnit, navGroupTitleKey, navLabelKey } from '../config/navigation';
// I18N-2B (2026-09-23): sidebar group titles/labels resolve through 'common'.
import { useTranslation } from 'react-i18next';
import { useEntitlements } from '../hooks/useEntitlements';
import { domainForModule, usePermissions } from '../hooks/usePermissions';
import { CondoOnboarding, UnitLinkCodeGenerator } from '../components/CondoLinking';
// 2026-09-24 (Jaime ruling): create-your-unit nudge for home-less users.
import CreateHomeNudge from '../components/CreateHomeNudge';

export const LensDashboard: React.FC = () => {
    const { user, roles, ownedUnits, activeOwnedUnitId, contexts, activeContextId, refreshMe } = useAuth();
    const location = useLocation();
    // Mensajes unread badge (2026-09-17): reads the single AppShell poller via event.
    const messagesUnread = useMessagesUnread();
    const navigate = useNavigate();
    // I18N-2B: sidebar group titles + labels resolve via 'common'.
    const { t } = useTranslation('common');
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const { entitlement, entitlementReady } = useEntitlements();
    // (2026-09-05 fail-closed) permissionsLoading drives the menu skeleton.
    const { can, loading: permissionsLoading } = usePermissions();
    // (2026-09-03) Non-residente Propietario Hogar lens: backend
    // /api/entitlements/me returns data.usuariosOnly=true + home module ids
    // stripped. Shared filterNavigation then limits the Hogar menu to Usuarios
    // (+ always-visible Mensajes/Quejas y Sugerencias); Condominio untouched.
    const usuariosOnly = entitlement?.usuariosOnly === true;

    const isSecurity = user?.role === 'security';
    // FIX 2026-09-07: backend role is 'empleado'; the old 'employee'
    // comparison was dead and never matched.
    const isEmployee = user?.role === 'empleado';

    // Parse lens from URL
    const lens = location.pathname.startsWith('/home') ? 'home' 
               : location.pathname.startsWith('/services') ? 'services' 
               : 'building';

    // Standalone homes (FIX 2026-08-31, rev2 - Bug B): the empty-state gate keys
    // on the ACTIVE UNIT's condo linkage (unit.condo_id), NOT on role or
    // permissions. The previous gate skipped super_admin entirely, so a super
    // admin viewing an unlinked ("Sin vincular") unit still got the condo
    // dashboard. Now: super admin + standalone unit -> CondoOnboarding CTA;
    // super admin + linked unit or any condo member (user.condoId set) ->
    // normal dashboard; security/employee lenses unchanged.
    const ownedUnitList = ownedUnits || [];
    const activeUnit = activeOwnedUnitId
        ? ownedUnitList.find((unit) => unit.id === activeOwnedUnitId)
        : undefined;
    const activeUnitUnlinked = activeUnit
        ? !activeUnit.condo_id
        : ownedUnitList.length > 0
            ? ownedUnitList.every((unit) => !unit.condo_id)
            : (roles || []).length === 0;
    const hasNoCondoContext = lens === 'building'
        && !isSecurity
        && !isEmployee
        && !(user?.condoId)
        && activeUnitUnlinked;

    // (2026-09-23, QA bugs #89/#91) Standalone-home lens: the ACTIVE unit has no
    // condo linkage and the user holds no condo context. The usuariosOnly strip
    // (backend /api/entitlements/me data.usuariosOnly for non-residente owners)
    // is a CONDO-context ruling; per spec a standalone home shows its unit
    // functions (plan baseline) + the link-to-condo CTA, never the
    // /home/staff-directory redirect.
    const isStandaloneHomeLens = !(user?.condoId) && isStandaloneHomeUnit(activeUnit, ownedUnitList);
    // (2026-09-23, bug #89) Condo-linked evidence for the usuariosOnly landing
    // redirect: only a user with a condo context (user.condoId or a condo-linked
    // owned unit) gets the /home/staff-directory redirect. Gated on positive
    // condo evidence so the auth-hydration window (ownedUnits still empty)
    // can never redirect a standalone-home owner.
    const condoLinkedOwner = !!(user?.condoId) || ownedUnitList.some((unit) => unit.condo_id);

    // Desktop groups are derived from the navigation registry so they stay in
    // lockstep with MobileMenu. (2026-09-05 legacy strip) There is NO separate
    // hard-coded employee menu anymore: the employee lens renders through the
    // same registry chain + standard gates ('Mis Pagos' removed 2026-09-07).
    const getSidebarGroups = () => {
        // (Jaime 2026-09-03) Registry-driven: derived from SERVICES_NAVIGATION so
        // desktop and any future consumer share one source.
        if (lens === 'services') {
            return SERVICES_NAVIGATION.map((group) => ({
                title: t(navGroupTitleKey(group.title), { defaultValue: group.title }),
                items: group.items.map((item) => ({ id: item.routes.condo, icon: item.icon, label: t(navLabelKey(item.key), { defaultValue: item.label }) })),
            }));
        }

        // (2026-09-05 fail-closed) While permissions are loading, render a
        // skeleton (no gated items) instead of flashing a wrong menu.
        if (permissionsLoading) {
            return [{ title: t('shell.loading'), items: [0, 1, 2].map((i) => ({ id: '', icon: Calculator, label: ' ' })) }];
        }

        const context = lens === 'home' ? 'home' : 'condo';
        // (Jaime 2026-08-31, TH1) Shared employee-context helper, identical to
        // MobileMenu's hamburger gate — desktop/mobile drift eliminated.
        // FIX (2026-09-03): pass the ACTIVE context so the employee gate is
        // scoped to the active unit/context (mixed-role users with a standalone
        // home active keep resident features).
        // FIX (2026-09-09, F136): computed BEFORE the security-lens branch — a
        // condo 'security' membership with sub_type 'empleado' is an employee
        // work context and must get the F136 employee keep-list, not the
        // plain security lens (which lacks Mi acceso/Reglamentos/Pánico/
        // Emergencias and shows Llave Digital, not in the ruling).
        const activeContext = (contexts || []).find((c) => c.id === activeContextId);
        // FIX (Jaime bug 2026-09-09 PM, Francisco Caripa): the global
        // `isEmployee ||` term leaked the employee strip into the user's OWN
        // home lens (global role 'empleado' is not context-scoped). The gate
        // is now lens-scoped: condo lens keeps the full work-context checks
        // (assigned unit / staff role / security+subType 'empleado'), home
        // lens strips ONLY for employee housing (activeUnit.assigned). A
        // standalone own home keeps Tareas/Compras/etc. on both menus.
        // isEmployee remains ONLY for the pure-employee condo-lens branch and
        // the hasNoCondoContext empty-state gate below.
        const employeeContext = isEmployeeContextForLens(context, user, ownedUnits, activeUnit, activeContext)
            || (lens === 'building' && isEmployee);
        // (Jaime 2026-09-03) Registry-driven security lens: verify + visitors +
        // smart-access now live in the registry with securityOnly flags.
        if (lens === 'building' && isSecurity && !employeeContext) {
            return [{ title: t(navGroupTitleKey('Seguridad'), { defaultValue: 'Seguridad' }), items: HAMBURGER_NAVIGATION
                .filter((item) => ['verify', 'visitors', 'smart-access'].includes(item.key))
                .map((item) => ({ id: item.routes.condo, icon: item.icon, label: t(navLabelKey(item.key), { defaultValue: item.label }) })) }];
        }
        // (Jaime 2026-09-03) Shared filterNavigation — the exact chain MobileMenu
        // runs, so desktop and mobile can never drift. alwaysVisible items are
        // filtered OUT of the scrollable groups here (they render as the fixed
        // pinned entries in IconGridSidebar's footer) to avoid duplicates.
        return DESKTOP_NAVIGATION[context].map((group) => ({
            title: t(navGroupTitleKey(group.title), { defaultValue: group.title }),
            items: filterNavigation(group.items, {
                // (2026-09-23, bugs #89/#91) Standalone-home lens: usuariosOnly is a
                // condo-context concept — pass the plan-baseline home ids so the
                // unit functions (Tareas/Compras/Presupuesto/Vehículos/Mascotas/
                // Equipos) are plan-gate visible. Condo lens keeps raw entitlement.
                entitlement: context === 'home' && isStandaloneHomeLens ? entitlementForStandaloneHome(entitlement) : entitlement,
                employeeContext, entitlementReady, user, roles, context,
                // Only the Hogar (home) context is usuariosOnly-restricted; the
                // Condominio lens keeps every Propietario function untouched.
                usuariosOnly: context === 'home' ? (usuariosOnly && !isStandaloneHomeLens) : false,
                isPermitted: (item) => !item.moduleId || item.moduleId === 'dashboard' || can(domainForModule(item.moduleId)),
            })
                .filter((item) => !item.alwaysVisible)
                .map((item) => ({
                    id: item.routes[context],
                    icon: item.icon,
                    label: t(navLabelKey(item.key), { defaultValue: item.label }),
                })),
        })).filter((group) => group.items.length > 0);
    };

    const handleNavigate = (id: string) => {
        if (!id) return; // skeleton placeholders are not navigable
        navigate(id);
    };

    // (2026-09-03) usuariosOnly landing: a non-residente Propietario hitting the
    // Hogar lens default route (Resumen/dashboard) is routed to Usuarios
    // (/home/staff-directory), the only substantive home page they can use.
    useEffect(() => {
        // (2026-09-23, bug #89) The redirect is a CONDO-context usuariosOnly
        // behavior: it requires a real condo context (user.condoId or a
        // condo-linked owned unit). Standalone-home owners keep /home/dashboard.
        // NOTE: gated on positively-linked condo evidence, NOT on
        // !isStandaloneHomeLens — during auth hydration ownedUnits is still
        // empty and isStandaloneHomeUnit would wrongly report false, causing
        // the exact QA bug #89 redirect on a hard load of /home/dashboard.
        if (lens === 'home' && usuariosOnly && condoLinkedOwner && location.pathname === '/home/dashboard') {
            navigate('/home/staff-directory', { replace: true });
        }
    }, [lens, usuariosOnly, condoLinkedOwner, location.pathname, navigate]);

    // Keep the shell mounted while rendering the page selected by the sidebar.
    // Mensajes is intentionally a standalone /mensajes route; its fixed sidebar entry lives in IconGridSidebar.
    // Unknown nested paths intentionally retain the old dashboard fallback.
    const renderContent = () => {
        switch (location.pathname) {
            case '/home/tasks': return <HomeTasks />;
            case '/home/shopping': return <HomeShopping />;
            case '/home/budget': return <HomeBudget />;
            case '/home/cars': return <HomeVehicles />;
            // (2026-09-24, wave-3) ES alias hard-nav /home/vehicles must land on
            // the Vehículos page, not the dashboard fallback (mirrors #104 /home/mascotas).
            case '/home/vehicles': return <HomeVehicles />;
            // (2026-09-24, bug #104) ES alias hard-nav /home/mascotas must land on
            // the pets page, not the dashboard fallback (mirrors /home/cars).
            case '/home/mascotas': return <HomePets />;
            case '/home/pets': return <HomePets />;
            case '/home/equipment': return <HomeEquipment />;
            case '/home/my-qr': return <MyQR />;
            case '/home/staff-directory': return <StaffDirectory />;
            case '/home/reservations': return <ReservationManager />;
            case '/home/turnover': return <TurnoverCoordination />;
            case '/home/staff-payroll': return <StaffPayroll />;
            case '/home/notifications': return <NotificationsCenter />;
            case '/building/notifications': return <NotificationsCenter />;
            case '/building/staff-directory': return <StaffDirectory />;
            case '/building/wall': return <WallPage />;
            case '/building/announcements': return <Announcements />;
            case '/building/calendar': return <Calendar />;
            case '/building/amenities': return <Amenities />;
            case '/building/voting':
            case '/building/votings': return <Voting />;
            case '/expenses':
            // (2026-09-25, bug #172) Home-lens sidebar Gastos navigates to /expenses
            // (config/navigation.ts home route); without this case it fell through
            // to the Dashboard2 fallback.
            case '/building/expenses': return <Expenses />;
            case '/building/employees': return <Employees />;
            // G1 (2026-10-01): member invite UI (propietario/residente/familiar/inquilino).
            case '/building/invites': return <InviteMember />;
            // Onboarding/frontend-self-serve (2026-10-01): admin units page — create units + link standalone units.
            case '/building/units': return <BuildingUnits />;
            case '/building/work-orders': return <WorkOrders />;
            case '/building/maintenance': return <Maintenance />;
            // (2026-09-19, F143-F145) Condo equipment registry (areas comunes).
            case '/building/equipment': return <CondoEquipment />;
            case '/building/morosos': return <Morosos />;
            case '/building/financial-reports': return <FinancialReports />;
            case '/building/reservations': return <ReservationManager />;
            case '/building/complaints': return <QuejasYSugerencias />;
            case '/building/regulations': return <Reglamentos />;
            case '/building/emergency': return <NumerosEmergencia />;
            case '/building/panic':
            case '/home/panic': return <PanicButton />;
            case '/building/resident-qr': return <MyQR />;
            case '/building/verify': return <VerifyQR />;
            // (2026-10-10, route re-parents) Lens-scoped cases for re-pointed nav targets.
            case '/home/travel': return <TravelNotices />;
            case '/building/vecinos': return <RosterVecinos />;
            case '/home/edicion-perfil': return <EdicionPerfil />;
            case '/building/visitors': return <Visitors />;
            case '/building/smart-access': return <LlaveDigital />;
            case '/building/join-requests': return <AdminJoinRequests />;
            case '/building/admin/qr-audit': return <QrAuditPage />;
            case '/building/visitor-report':
            case '/building/reporte-visitas': return <ReporteVisitas />;
            case '/building/caja-chica': return <CajaChica />;
            case '/services/suppliers': return <SupplierDirectory />;
            case '/services/employees': return <Employees />;
            case '/services/supplier-dashboard': return <SupplierDashboard />;
            case '/services/work-orders': return <WorkOrders />;
            case '/services/promotions': return <Marketplace />;
            // Dashboard destinations are the only intentional dashboard fallbacks.
            case '/services/dashboard': return <Marketplace />;
            case '/home/dashboard':
            case '/building/dashboard':
                // Onboarding checklist (Jaime GO 2026-10-01): new users land here.
                return <><OnboardingChecklist /><Dashboard2 forceLens={lens} /></>;
            default: return <Dashboard2 forceLens={lens} />;
        }
    };

    // The wall page is a chat layout: its message list must scroll while the
    // composer stays pinned. That requires a definite height, so the wrapper
    // uses h-full (not min-h-full) for that route — otherwise WallPage's h-full
    // resolves against an auto-height parent and the whole page scrolls instead.
    const isWallChat = location.pathname === '/building/wall';
    if (hasNoCondoContext) {
        return (
            <div className="w-full overflow-auto bg-[var(--v-base-bg)]">
                <div className="p-4 md:p-6 min-h-full flex items-center justify-center">
                    <CondoOnboarding
                        activeUnitId={activeOwnedUnitId}
                        onLinked={() => { refreshMe().catch(() => undefined); }}
                    />
                    {/* Onboarding/frontend-enable (2026-10-01): standalone-unit
                        owners without condo context generate their 8-char link
                        code here (LIVE GET /api/units/my/link-code). */}
                    <UnitLinkCodeGenerator unitId={activeOwnedUnitId} />
                </div>
            </div>
        );
    }
    return (
        <div className="flex w-full h-full bg-[var(--v-base-bg)]">
            <div className="hidden md:block h-full border-r border-[var(--v-border-light)]">
                <IconGridSidebar 
                    messagesUnread={messagesUnread}
                    groups={getSidebarGroups()}
                    activeItem={location.pathname} 
                    onItemClick={handleNavigate}
                    isCollapsed={sidebarCollapsed}
                    onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
                    context={lens === 'home' ? 'home' : 'condo'}
                />
            </div>
            <div className={`flex-1 overflow-auto bg-[var(--v-base-bg)] relative ${isWallChat ? 'flex flex-col' : ''}`}>
                 <div className={isWallChat ? 'flex-1 min-h-0 p-4 md:p-6 flex' : 'p-4 md:p-6 min-h-full'}>
                 {lens === 'home' && !isWallChat && <CreateHomeNudge />}
                 {renderContent()}
                 </div>
            </div>
        </div>
    );
};
